import React from "react";
import MathText from "./pages/MathText.tsx";

interface Option {
  text?: string;
  value?: string;
  is_correct?: boolean;
  correct_reason?: string;
  incorrect_reason?: string;
}

interface Question {
  question_id: string;
  question: string;
  options: (string | Option)[];
  score?: number;
  marks?: number;
}

interface Props {
  q: Question;
  index: number;
  selected?: string;
  onSelect: (qid: string, option: string) => void;
  onClear?: (qid: string) => void;
  testSubmitted?: boolean; // 👈 important
}

const QuestionCard: React.FC<Props> = ({
  q,
  index,
  selected,
  onSelect,
  onClear,
  testSubmitted = false,
}) => {
  const getOptionValue = (opt: string | Option): string => {
  if (typeof opt === "string") return opt;
  return opt.value || opt.text || "";
};

const getOptionLabel = (opt: string | Option): string => {
  if (typeof opt === "string") return opt;
  return opt.text || opt.value || "";
};

  return (
    <div
      style={{
        marginBottom: "16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ padding: "16px" }}>
        {/* Question Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "12px",
            gap: "16px",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "15px",
              flex: 1,
              lineHeight: "1.5",
            }}
          >
            <span style={{ color: "#667eea", marginRight: "8px" }}>
              Q{index + 1}.
            </span>
            <MathText text={q?.question || ""} />
          </div>

          {(q?.score || q?.marks) && (
            <div
              style={{
                backgroundColor: "#9c27b0",
                color: "white",
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {q?.score || q?.marks} Marks
            </div>
          )}
        </div>

        {/* Options */}
        <div>
          {q?.options?.map((opt: string | Option, i: number) => {
            const optValue = getOptionValue(opt);
            const optLabel = getOptionLabel(opt);
            const isSelected = selected === optValue;

            const optionObj =
              typeof opt === "string" ? null : (opt as Option);

            const isCorrect = optionObj?.is_correct === true;

            // After submit → show correct answer highlight
            const showCorrectHighlight =
              testSubmitted && optionObj?.is_correct;

            return (
              <div key={i}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "6px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: isSelected
  ? testSubmitted
    ? isCorrect
      ? "2px solid #4caf50"
      : "2px solid #f44336"
    : "2px solid #667eea" 
  : testSubmitted && showCorrectHighlight
  ? "2px solid #4caf50"
  : "1px solid #e0e0e0",

backgroundColor: isSelected
  ? testSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "#eef2ff" // neutral light blue during test
  : testSubmitted && showCorrectHighlight
  ? "#e8f5e9"
  : "transparent",
                    cursor: testSubmitted ? "default" : "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <input
                    type="radio"
                    name={`question-${q.question_id}`}
                    value={optValue}
                    checked={isSelected}
                    disabled={testSubmitted} // 🔒 lock after submit
                    onChange={(e) =>
                      onSelect(q.question_id, e.target.value)
                    }
                    style={{
                      marginRight: "12px",
                      width: "18px",
                      height: "18px",
                      cursor: testSubmitted ? "default" : "pointer",
                      accentColor: testSubmitted
  ? isSelected
    ? isCorrect
      ? "#4caf50"
      : "#f44336"
    : "#667eea"
  : "#667eea", // always blue during test

                    }}
                  />

                  <span
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                      flex: 1,
                    }}
                  >
                    <MathText text={optLabel} />
                  </span>
                </label>

                {/* 🔥 Reasoning Section */}
                {testSubmitted && isSelected && optionObj && (
                  <div
                    style={{
                      marginLeft: "36px",
                      marginBottom: "12px",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      fontSize: "13px",
                      backgroundColor: isCorrect
                        ? "#f1f8e9"
                        : "#fff3e0",
                      border: `1px solid ${
                        isCorrect ? "#8bc34a" : "#ff9800"
                      }`,
                    }}
                  >
                    {isCorrect ? (
                      <>
                        <strong style={{ color: "#2e7d32" }}>
                          ✔ Correct:
                        </strong>{" "}
                        {optionObj.correct_reason ||
                          "Well done!"}
                      </>
                    ) : (
                      <>
                        <strong style={{ color: "#d32f2f" }}>
                          ✘ Incorrect:
                        </strong>{" "}
                        {optionObj.incorrect_reason ||
                          "This option is not correct."}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Clear Button */}
        {onClear && selected && !testSubmitted && (
          <button
            onClick={() => onClear(q?.question_id)}
            style={{
              marginTop: "12px",
              padding: "6px 16px",
              backgroundColor: "transparent",
              color: "#d32f2f",
              border: "1px solid #d32f2f",
              borderRadius: "4px",
              fontSize: "13px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            Clear Answer
          </button>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;
